import { createContext, useContext, useState, useEffect } from "react";
import { saveAuth, clearAuth, getToken, getRole } from "../utils/auth";
import { decodeToken, isTokenExpired } from "../utils/jwt";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext<any>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const token = getToken();
      if (token) {
        // Scenario 3: Validate token expiry on app load
        if (isTokenExpired(token)) {
          clearAuth();
          localStorage.removeItem("user");
          localStorage.removeItem("tenant");
        } else {
          // Decode token to get claims (Scenario 2: User ID, Role, Tenant ID)
          const claims = decodeToken(token);
          if (claims) {
            const storedUser = localStorage.getItem("user");
            const storedTenant = localStorage.getItem("tenant");
            if (storedUser) setUser(JSON.parse(storedUser));
            if (storedTenant) setTenant(JSON.parse(storedTenant));
          }
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[Auth] Token validation failed on load:", err);
      }
    }
    setLoading(false);
  }, []);

  const loginUser = (
    token: string,
    role: string,
    isSubscribed?: boolean,
    userData?: any,
    tenantData?: any,
    mustChangePassword?: boolean
  ) => {
    saveAuth(token, role, isSubscribed);
    if (userData) {
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
    }
    if (tenantData) {
      setTenant(tenantData);
      localStorage.setItem("tenant", JSON.stringify(tenantData));
    }

    // Scenario 1: First login - temporary password, redirect to Change Password page
    if (mustChangePassword === true) {
      navigate("/change-password");
      return;
    }

    if (role === "TENANT_ADMIN" && isSubscribed === false) {
      navigate("/subscription");
      return;
    }
    if (role === "SUPER_ADMIN") {
      navigate("/dashboard");
    } else if (role === "ADMIN" || role === "TENANT_ADMIN" || role === "DOCTOR" || role === "HOSPITAL_USER") {
      navigate("/admin");
    } else {
      navigate("/dashboard");
    }
  };

  // Scenario 2: Called after the first-login password change completes.
  // Swaps the old token for the fresh one and clears the must-change flag.
  // The calling page handles navigation (success screen -> dashboard).
  const completePasswordChange = (token: string, role: string, userData?: any, tenantData?: any) => {
    const isSubscribed = localStorage.getItem("isSubscribed") === "true" || undefined;
    saveAuth(token, role, isSubscribed);
    if (userData) {
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
    }
    if (tenantData) {
      setTenant(tenantData);
      localStorage.setItem("tenant", JSON.stringify(tenantData));
    }
  };

  const updateUser = (updatedFields: Record<string, any>) => {
    setUser((prev: any) => {
      const updated = { ...(prev || {}), ...updatedFields };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  };

  const logout = () => {
    clearAuth();
    localStorage.removeItem("user");
    localStorage.removeItem("tenant");
    setUser(null);
    setTenant(null);
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{ loginUser, completePasswordChange, updateUser, logout, user, tenant, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
